import { Badge } from '@/components/Atoms/Badge/Badge';
import { Icon, IconSize } from '@/components/Atoms/Icon/Icon';
import Terminal from '@/components/Icons/Terminal';
import useTranslation from '@/hooks/useTranslation';
import type { DatasetType, PublicWebinarSubtype } from '@/interfaces';
import { ContentType } from '@/interfaces';
import type { Tool } from 'bnef';
import type { ComponentProps } from 'react';
import type { FCl } from 'react-fc';

/**
 * A function to generate the correct search URL for the specific type
 */
const generateSearchUrl = (headerType: ContentType) => {
    switch (headerType) {
        case ContentType.insight:
            return '/insights';
        case ContentType.theme:
            return '/themes';
        case ContentType.short:
            return '/shorts';
        case ContentType.analystreaction:
            return '/analyst-reactions';
        case ContentType.news:
            return '/news';
        case ContentType.video:
            return '/videos';
        case ContentType.countryprofile:
            return '/countries';
        case ContentType.interactivedataset:
            return '/interactive-datasets';
        case ContentType.event:
        case ContentType.webinar:
        case ContentType.publicwebinar:
        case ContentType.publication:
        case ContentType.summit:
            return '/events';
        case ContentType.flagship:
            return '/flagships';
        case ContentType.series:
            return '/series';
        case ContentType.sector:
            return '/sectors';
        case ContentType.policy:
            return '/policies';
        default:
            return null;
    }
};

const generatePillColors = (contentType: ContentType) => {
    switch (contentType) {
        case ContentType.news:
            return 'grey';
        case ContentType.interactivedataset:
            return 'green';
        case ContentType.summit:
            return 'teal';
        case ContentType.data:
        case ContentType.tool:
        case ContentType.policy:
            return 'purple';
        default:
            return 'blue';
    }
};

// For certain views we'll use a ContentCard for both ContentTypes and DatasetTypes (ie: Quick Search results)
const isContentType = (itemType: ContentType | DatasetType) => {
    return itemType in ContentType;
};

/**
 * A component for adding a "type" header, correctly translated.
 */
export const ContentTypeHeader = ({ type, eventSubtype, className, textOverride }: IProps) => {
    const searchUrl = generateSearchUrl(type);
    const { t } = useTranslation();

    let icon;
    if (type === ContentType.terminal) {
        icon = <Icon use={<Terminal />} size={IconSize.EXTRA_SMALL} />;
    }

    // geoeconomics temp hack. tidy up later
    textOverride = type === ContentType.geoeconomics ? 'Analysis' : textOverride;

    const translatedType = isContentType(type) ? t(`ContentType.${type}`) : t(`DataType.${type}`);

    return (
        <Badge
            className={className}
            text={textOverride || (eventSubtype ? t(`Event.Subtype.${eventSubtype}`) : translatedType)}
            link={searchUrl}
            icon={icon}
            colorVar={generatePillColors(type)}
        />
    );
};

export const ToolTypeHeader: FCl<{ subtype: Tool['subtype'] }> = ({ subtype, className }) => {
    const { t } = useTranslation();
    let color: ComponentProps<typeof Badge>['colorVar'] = 'purple';
    if (['interactive-dataset', 'insight-dataset', 'excel-model'].includes(subtype)) {
        color = 'green';
    }

    return <Badge className={className} text={t(`ContentType.${subtype}`)} colorVar={color} />;
};

interface IProps {
    type: ContentType;
    eventSubtype?: PublicWebinarSubtype;
    className?: string;
    textOverride?: string;
}
